import { useEffect, useEffectEvent, useState } from 'react';
import {
    getDefaultScheduleDate,
    getDefaultScheduleTime,
    getDefaultTimezone,
    validateScheduleTime,
} from '@/features/email-flows/logic/timeValidation.logic';
import { ScheduleData } from '../components/ScheduleBroadcastModal';

interface UseScheduleBroadcastModalProps {
    isOpen: boolean;
    savedSchedule?: ScheduleData;
    onSchedule: (schedule: ScheduleData) => Promise<void>;
}

export const useScheduleBroadcastModal = ({ isOpen, savedSchedule, onSchedule }: UseScheduleBroadcastModalProps) => {
    const [selectedDate, setSelectedDate] = useState<Date>(getDefaultScheduleDate());
    const [selectedTime, setSelectedTime] = useState<number>(getDefaultScheduleTime());
    const [selectedTimezone, setSelectedTimezone] = useState<string>(getDefaultTimezone());
    const [errorMessage, setErrorMessage] = useState<string>('');

    // Update state when modal opens with savedSchedule
    const initializeSchedule = useEffectEvent((schedule?: ScheduleData) => {
        if (schedule) {
            setSelectedDate(schedule.selectedDate);
            setSelectedTime(schedule.selectedTime);
            setSelectedTimezone(schedule.selectedTimezone);
        } else {
            setSelectedDate(getDefaultScheduleDate());
            setSelectedTime(getDefaultScheduleTime());
            setSelectedTimezone(getDefaultTimezone());
        }
    });

    const clearErrorIfValid = useEffectEvent((date: Date, time: number, error: string) => {
        if (error) {
            const validation = validateScheduleTime(date, time);
            if (validation.isValid) {
                setErrorMessage('');
            }
        }
    });

    useEffect(() => {
        // Only update when modal is actually open
        if (!isOpen) return;
        initializeSchedule(savedSchedule);
    }, [isOpen]);

    // Clear error when date or time changes
    useEffect(() => {
        clearErrorIfValid(selectedDate, selectedTime, errorMessage);
    }, [selectedDate, selectedTime]);

    const handleSchedule = () => {
        const validation = validateScheduleTime(selectedDate, selectedTime);
        if (!validation.isValid) {
            setErrorMessage(validation.error || 'Invalid schedule');
            return;
        }

        // Clear error if validation passes
        setErrorMessage('');

        onSchedule({
            selectedDate,
            selectedTime,
            selectedTimezone,
        });
    };

    return {
        selectedDate,
        selectedTime,
        selectedTimezone,
        errorMessage,
        setSelectedDate,
        setSelectedTime,
        setSelectedTimezone,
        handleSchedule,
    };
};
